import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useOnline, useServiceWorker } from '../lib/service-worker.js';

/** Bottom-Navigation (SPEC 8.1). */
const NAV = [
  { to: '/', label: 'Boards', icon: '▦' },
  { to: '/meine-aufgaben', label: 'Meine Aufgaben', icon: '☑' },
  { to: '/suche', label: 'Suche', icon: '⌕' },
  { to: '/profil', label: 'Profil', icon: '☺' },
] as const;

export function AppShell({ children }: { children: ReactNode }) {
  const online = useOnline();
  const { updateReady, applyUpdate } = useServiceWorker();

  return (
    <div className="flex h-full flex-col">
      {/*
        Offline-Banner am oberen Rand, nicht wegklickbar solange offline
        (SPEC 9.7). Es nimmt Platz weg statt zu ueberlagern — wer offline ist,
        soll nicht gegen eine Schaltflaeche tippen, die gerade nichts tut.
      */}
      {!online && (
        <p
          role="status"
          data-testid="offline-banner"
          className="shrink-0 bg-warning-soft px-4 py-1.5 text-center text-xs text-warning"
        >
          Offline — Änderungen sind gerade nicht möglich
        </p>
      )}

      {updateReady && (
        <div className="flex shrink-0 items-center justify-between gap-3 bg-accent-soft px-4 py-1.5 text-xs text-accent">
          <span>Neue Version verfügbar</span>
          <button
            type="button"
            data-testid="apply-update"
            onClick={applyUpdate}
            className="rounded-[var(--radius-sm)] border border-accent px-2 py-0.5 font-semibold"
          >
            Neu laden
          </button>
        </div>
      )}

      <main className="min-h-0 flex-1 overflow-hidden pb-[var(--bottom-nav-height)]">
        {children}
      </main>

      <nav
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface pb-[env(safe-area-inset-bottom)]"
        aria-label="Hauptnavigation"
      >
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center justify-center gap-0.5 text-[0.6875rem] ${
                isActive ? 'text-accent' : 'text-muted'
              }`
            }
            style={{ minHeight: 'var(--bottom-nav-height)' }}
          >
            <span aria-hidden="true" className="text-base">
              {item.icon}
            </span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
